// One product line per page under /merch/<line>.
//
// "Palworld TCG merchandise" has no established owner: generalist listicles rank
// with no prices and no photos. Each line here gets its own indexable page with
// the real product photo, the live marketplace price, Product schema and both
// buy buttons, so the page answers a buying query end to end.
import { SEALED_BY_KEY, type SealedProduct } from "./prices-shared";
import { SEALED_IMAGES, type SealedImage } from "./sealed-images";

export interface MerchLine {
  slug: string;
  /** Tracked sealed/merch product key whose live price this page shows. */
  key: string;
  /** H1, kept leading with "Palworld". */
  h1: string;
  title: string;
  description: string;
  /** Answer-first opening paragraph (AEO). */
  answer: string;
  /** Licensee credited on the packaging for this line. */
  licensee: string;
  /** What to check before buying — three to five short checks. */
  checks: string[];
  faq: { q: string; a: string }[];
}

export const MERCH_LINES: MerchLine[] = [
  {
    slug: "plush",
    key: "merch-plush",
    h1: "Palworld Plush",
    title: "Palworld Plush: Official Plushies & Live Prices",
    description:
      "Official Palworld plush prices: which Pals have licensed plushies, the live marketplace price, how to spot fan-made copies, and where to buy.",
    answer:
      "Official Palworld plushies are produced under licence by The Loyal Subjects and cover a subset of the roster rather than every Pal, with typical retail around $25 for a standard size. A licensed plush always carries a sewn tag naming the licensee and a Pocketpair copyright line; a plush of a Pal with no announced line is fan-made regardless of how the listing is worded.",
    licensee: "The Loyal Subjects",
    checks: [
      "A sewn licensee tag with a Pocketpair copyright line — not a printed sticker.",
      "Real product photography from more than one angle, not a single render.",
      "Normal retail shipping estimates rather than three to four weeks from overseas.",
      "The Pal itself is an officially released Pal, which you can confirm in the Pal database.",
    ],
    faq: [
      {
        q: "Are there official Palworld plushies?",
        a: "Yes. Plush and blind-box lines are licensed to The Loyal Subjects and cover a subset of the roster. If a Pal has no announced plush, any plush of it is fan-made.",
      },
      {
        q: "How much does a Palworld plush cost?",
        a: "Standard-size official plush sits around $25 at retail, with larger sizes and limited runs higher. The live price on this page is read from current marketplace listings, so it shows what sellers are actually asking today rather than the launch price.",
      },
      {
        q: "How can I tell a fake Palworld plush?",
        a: "Check the sewn tag for the licensee and Pocketpair copyright, look for real multi-angle photos, and treat a long overseas shipping window as a warning sign. Fan-made plush is usually print-on-demand or hand-sewn with no tag at all.",
      },
    ],
  },
  {
    slug: "figures",
    key: "merch-figure",
    h1: "Palworld Figures",
    title: "Palworld Figures: Official Statues & Live Prices",
    description:
      "Official Palworld figure prices: licensed statues and collectables, live marketplace value, pre-order windows, and how to avoid unlicensed resin copies.",
    answer:
      "Official Palworld figures are allocation-produced collectables, typically around $40 and up at retail, and they generally do not restock once the announced pre-order window closes — remaining supply moves to resale at a premium. Buying inside the pre-order window is the only reliable way to pay retail, and any figure sold as unpainted or hand-cast resin is unlicensed.",
    licensee: "Bushiroad and announced figure partners",
    checks: [
      "The figure is an announced licensed release, not a hand-cast resin kit.",
      "Box art carries the licensee and a Pocketpair copyright line.",
      "Pre-order windows and shipping quarters are stated up front.",
      "Resale pricing is compared against the last retail price before you pay a premium.",
    ],
    faq: [
      {
        q: "How much are Palworld figures?",
        a: "Licensed figures start around $40 at retail and rise with scale and edition size. The live price above tracks current marketplace listings, which is usually above retail once a pre-order window has closed.",
      },
      {
        q: "Do Palworld figures restock?",
        a: "Allocation-produced figures generally do not restock at retail. After the pre-order window, supply moves to resale, which is why the marketplace price can sit well above the original price.",
      },
      {
        q: "Are unpainted resin Palworld figures official?",
        a: "No. Hand-cast or unpainted resin figures are unlicensed fan production. Official releases ship painted, in printed retail packaging that names the licensee.",
      },
    ],
  },
  {
    slug: "playmat",
    key: "merch-playmat",
    h1: "Palworld TCG Playmat",
    title: "Palworld TCG Playmat: Official Mat Prices",
    description:
      "Official Palworld card game playmat prices: rubber mat sizing, why a mat protects card value, live marketplace price and where to buy.",
    answer:
      "The official Palworld card game playmat is a rubber-backed cloth mat sized for a standard two-player card game layout, priced around $25 at retail. It is the cheapest protection for card value in play: a mat keeps sleeved cards off abrasive table surfaces, which is where most surface scratching on played cards actually happens.",
    licensee: "Bushiroad",
    checks: [
      "Rubber backing with a stitched or heat-sealed edge, not a thin printed cloth.",
      "Official art with the licensee and copyright line printed in the margin.",
      "Standard playmat dimensions, so card zones line up with tournament layouts.",
      "Listing photos show the actual mat, not a flat art mock-up.",
    ],
    faq: [
      {
        q: "How much is the official Palworld playmat?",
        a: "Retail sits around $25, and the live figure above is read from current marketplace listings. Promotional and event mats trade above that because print runs were limited.",
      },
      {
        q: "Do I need a playmat for the Palworld TCG?",
        a: "For casual play at home it is optional, but it is the cheapest way to stop surface wear on sleeved cards, and most organised events expect one. On a chase card, one scratch costs more than the mat.",
      },
      {
        q: "Are unofficial Palworld playmats safe to use?",
        a: "Print-on-demand mats work mechanically but the print quality and edge stitching vary widely, and they are not licensed. If you are protecting valuable cards, a stitched official mat with rubber backing is the safer buy.",
      },
    ],
  },
  {
    slug: "card-sleeves",
    key: "merch-sleeves",
    h1: "Palworld TCG Card Sleeves",
    title: "Palworld Card Sleeves: Official Sleeve Prices",
    description:
      "Official Palworld TCG card sleeve prices: sizing, pack counts, when to replace sleeves, live marketplace price and where to buy.",
    answer:
      "Official Palworld TCG sleeves are standard-size deck protectors, sold in packs around $10 at retail, and they are the single cheapest way to keep a card's condition — and therefore its price — intact. Sleeve every card you actually play, use a fresh pack for graded submissions, and replace sleeves once they cloud or the edges split.",
    licensee: "Bushiroad",
    checks: [
      "Standard card size, so sleeves fit without bowing the card.",
      "Pack count printed on the wrapper and matching the listing.",
      "Sealed packaging naming the licensee, not a loose bundle.",
      "Opaque backs of even thickness — thin sleeves let corners wear anyway.",
    ],
    faq: [
      {
        q: "How much are Palworld card sleeves?",
        a: "A pack of official sleeves sits around $10 at retail, and the figure above tracks current marketplace listings. Limited art sleeves from events sell higher.",
      },
      {
        q: "Do sleeves protect Palworld card value?",
        a: "Yes, meaningfully. Most value loss on a played card is surface scuffing and corner wear, both of which sleeves prevent. A card that stays near-mint keeps the raw market price and remains a candidate for grading.",
      },
      {
        q: "When should I replace card sleeves?",
        a: "Replace them when the surface clouds, the edges split, or the sleeve no longer grips the card. Worn sleeves stop protecting and start abrading the card's edges.",
      },
    ],
  },
  {
    slug: "binder-storage",
    key: "merch-binder",
    h1: "Palworld TCG Binder & Storage",
    title: "Palworld TCG Binder & Storage Box Prices",
    description:
      "Official Palworld TCG binder and storage box prices: pocket counts, side-loading pages, live marketplace price and where to buy.",
    answer:
      "Official Palworld card storage covers binders and rigid storage boxes at around $20 at retail. For anything you care about, choose a side-loading binder with fixed pocket pages — top-loading pockets let cards slide out when the binder is upended, which is the most common way a collection gets damaged in a bag.",
    licensee: "Bushiroad",
    checks: [
      "Side-loading pockets rather than top-loading pages.",
      "Rigid covers and a strap or zip closure for transport.",
      "Acid-free pocket material, so long-term storage does not haze the card face.",
      "A stated pocket count that matches how you sort your collection.",
    ],
    faq: [
      {
        q: "How much is a Palworld card binder?",
        a: "Official binders and storage boxes sit around $20 at retail, with the live number above read from current marketplace listings.",
      },
      {
        q: "Is a side-loading binder better for Palworld cards?",
        a: "Yes. Side-loading pockets keep cards in place when the binder tips, while top-loading pages let them slide free and take corner damage. It is the single most important feature to check.",
      },
      {
        q: "Should cards be sleeved inside a binder?",
        a: "For chase printings, yes: a sleeve inside a pocket protects against friction when pages are turned. Use pockets rated for sleeved cards so the page does not bow.",
      },
    ],
  },
  {
    slug: "apparel",
    key: "merch-apparel",
    h1: "Palworld Apparel",
    title: "Palworld Apparel: Official Shirts & Hoodie Prices",
    description:
      "Official Palworld apparel prices: licensed shirts and hoodies, sizing and print quality checks, live marketplace price and where to buy.",
    answer:
      "Official Palworld apparel covers licensed shirts and hoodies at roughly $30 and up at retail. Licensed pieces list the licensee on the neck label or garment tag and ship on normal retail timelines; unlicensed drop-ship apparel is the largest unofficial category in Palworld merchandise and is usually identifiable by a mock-up-only photo and a long overseas shipping window.",
    licensee: "Announced apparel licensees",
    checks: [
      "A printed neck label or garment tag naming the licensee.",
      "Photos of the real garment, not an art mock-up on a blank template.",
      "A stated size chart in actual measurements, not just S–XXL.",
      "Retail shipping timelines rather than three to four weeks from overseas.",
    ],
    faq: [
      {
        q: "How much is official Palworld apparel?",
        a: "Shirts and hoodies start around $30 at retail and rise with limited collaborations. The figure above is the live marketplace asking price.",
      },
      {
        q: "How do I spot unlicensed Palworld shirts?",
        a: "Mock-up-only images, no size chart in measurements, and a three-to-four-week overseas shipping window together mean print-on-demand. Licensed garments name their licensee on the tag.",
      },
      {
        q: "Does Palworld apparel run small?",
        a: "Sizing varies by licensee, so read the measured size chart rather than the letter size. Where a listing gives no measurements, treat it as an unlicensed print-on-demand item.",
      },
    ],
  },
  {
    slug: "keychains-pins",
    key: "merch-keychain",
    h1: "Palworld Keychains & Pins",
    title: "Palworld Keychains & Pins: Official Prices",
    description:
      "Official Palworld keychain, acrylic charm and pin badge prices: licensed small goods, how packaging is marked, live marketplace price and where to buy.",
    answer:
      "Official Palworld keychains, acrylic charms and pin badges are small-goods lines at around $13 at retail, and they are the category where unlicensed sellers concentrate because production is cheap. A licensed item arrives on a printed backing card naming the licensee with a Pocketpair copyright line; loose acrylic in a bare polybag is fan-made.",
    licensee: "Announced small-goods licensees",
    checks: [
      "A printed backing card or blister naming the licensee.",
      "Metal hardware rather than a plain split ring on a bare acrylic cut.",
      "Photos of the actual item next to its packaging.",
      "Blind-box odds stated on the box where a line is sold blind.",
    ],
    faq: [
      {
        q: "How much are Palworld keychains?",
        a: "Official keychains, charms and pins sit around $13 at retail, with the live number above read from current marketplace listings. Blind-box chase variants sell higher.",
      },
      {
        q: "Are acrylic Palworld charms official?",
        a: "Only when they arrive on printed licensed packaging. Loose acrylic charms in bare polybags are the most common unlicensed Palworld item.",
      },
      {
        q: "Are Palworld blind boxes worth buying?",
        a: "They suit collectors who want the full set, because a sealed case guarantees the case ratio while single blind boxes do not guarantee any specific figure. If you want one specific character, buying it opened is usually cheaper.",
      },
    ],
  },
  {
    slug: "sleeve-card-set",
    key: "merch-sleeve-set",
    h1: "Palworld Sleeve & Card Set Vol. 1",
    title: "Palworld Sleeve & Card Set Vol. 1 Price",
    description:
      "Palworld Official Card Game Sleeve & Card Set Vol. 1: what is in the set, the promo card included, live marketplace price and where to buy.",
    answer:
      "The Palworld OFFICIAL CARD GAME Sleeve & Card Set Vol. 1 is a Bushiroad accessory bundle that pairs a run of standard-size art sleeves with an exclusive promo card, priced around $15 at retail. Because the promo card is only available in this set, sealed copies hold value better than plain sleeve packs — if you want the card, buy the set sealed rather than opened.",
    licensee: "Bushiroad",
    checks: [
      "Listing states the set is sealed, so the promo card is still inside.",
      "Sleeve count printed on the packaging matches the listing description.",
      "The wrapper names Bushiroad with a Pocketpair copyright line.",
      "\"Vol. 1\" is printed on the box — later volumes carry different promo cards.",
    ],
    faq: [
      {
        q: "What comes in Palworld Sleeve & Card Set Vol. 1?",
        a: "A run of standard-size official art sleeves plus one exclusive promo card that is not sold separately. That promo is the reason the set trades above a plain sleeve pack.",
      },
      {
        q: "How much is Sleeve & Card Set Vol. 1?",
        a: "Retail sits around $15, and the live figure on this page is read from current marketplace listings. Sealed copies ask more than opened ones because of the promo card.",
      },
      {
        q: "Should I open Sleeve & Card Set Vol. 1?",
        a: "If you want to play with the sleeves, yes. If you are collecting, an unopened set keeps the promo card in mint condition and holds resale value better than a used sleeve pack.",
      },
    ],
  },
  {
    slug: "legends-awaken",
    key: "merch-legends-awaken",
    h1: "Palworld Legends Awaken Merchandise",
    title: "Palworld Legends Awaken Merch & Accessory Prices",
    description:
      "Palworld Legends Awaken merchandise: set-branded sleeves, playmats, deck boxes and storage, live marketplace price and where to buy sealed product.",
    answer:
      "Legends Awaken (EBP02) merchandise is the set-branded accessory wave that ships alongside the second Palworld card set: sleeves, playmats, deck boxes and storage carrying Legends Awaken key art, typically around $25 and up. Set-branded accessories are printed for one wave only, so they behave more like limited product than restocking staples — the plain-art equivalents stay available while these do not.",
    licensee: "Bushiroad",
    checks: [
      "Packaging shows the Legends Awaken set logo, not generic Palworld art.",
      "The item is an accessory, not a sealed booster product listed under merch.",
      "Real product photos rather than the announcement key visual alone.",
      "Compare against the plain-art equivalent before paying a set premium.",
    ],
    faq: [
      {
        q: "What Legends Awaken merchandise is there?",
        a: "The wave covers set-branded card-game accessories — sleeves, playmats, deck boxes and storage — using Legends Awaken key art. Sealed booster product for the set is tracked separately on the price board.",
      },
      {
        q: "How much does Legends Awaken merch cost?",
        a: "Accessories start around $25 at retail depending on the item, with the live figure above read from current marketplace listings. Set-branded runs drift above retail once the wave sells through.",
      },
      {
        q: "Is Legends Awaken accessory art reprinted?",
        a: "Generally no. Set-branded art is tied to one release wave, so once stock clears it moves to resale. Plain-art sleeves and mats remain in production if you only need protection.",
      },
    ],
  },
];

export const MERCH_LINE_BY_SLUG = new Map(MERCH_LINES.map((l) => [l.slug, l]));

export function merchProduct(line: MerchLine): SealedProduct | undefined {
  return SEALED_BY_KEY[line.key];
}

export function merchImage(line: MerchLine): SealedImage | undefined {
  return SEALED_IMAGES[line.key];
}
